import { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { format } from 'date-fns';
import { FormModal } from './FormModal';
import { FileInput } from '../inputs';
import { useNotify } from '../../../hooks';
import { authPost } from '../../../lib';
import { FileType } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => any;
  customerId: number | string;
}

export const CustomerItemImportForm = ({
  open,
  handleClose,
  customerId,
}: Props) => {
  const { classes, cx } = useStyles();
  const notify = useNotify();

  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<Array<FileType>>([]);

  const handleSubmit = useCallback(async () => {
    if (!files.length) {
      return notify('Please upload a file', 'error');
    }
    setSubmitting(true);

    const formData = new FormData();
    formData.append('customerID', String(customerId));
    formData.append('File', files[0].rawFile);

    const { error } = await authPost(
      `/customers/${customerId}/items/file-import`,
      formData,
      undefined,
      undefined,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    setSubmitting(false);
    if (error) {
      notify(
        error.message || error.Message || error.title || 'Something went wrong',
        'error',
      );
    } else {
      notify(
        'Item import is in progress. You will be notified when it completes.',
        'success',
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, customerId]);

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Import items</span>}
      callback={handleSubmit}
      btnText='SAVE'
      maxWidth='lg'
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
    >
      <div className={classes.layout}>
        <div className={classes.fileInput}>
          <FileInput
            files={files}
            setFiles={setFiles}
            filePrefix={`customer-items/upload-${format(new Date(), 'Pp')}`}
            multiple={false}
            acceptFileTypes={{ 'text/*': ['.txt', '.csv'] }}
            uploadText={
              <div className={classes.inputTextContainer}>
                <span className={classes.inputText}>
                  Drag 'n' drop a file here, or click to select files
                </span>
                <span className={cx(classes.inputText, classes.inputSmallText)}>
                  File should be in CSV format
                </span>
              </div>
            }
          />
        </div>
      </div>
    </FormModal>
  );
};

const useStyles = makeStyles({ name: { CustomerItemImportForm } })((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  fileInput: {
    marginTop: 70,
  },
  inputTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  inputText: {
    color: '#A5AABD',
    fontSize: 16,
    letterSpacing: 0,
  },
  inputSmallText: {
    fontSize: 12,
  },
}));
